import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Mail, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { apiErrorMessage } from '../lib/utils';

const VerificationModal = ({ isOpen, onClose, type, onSuccess }) => {
  const [step, setStep] = useState('send'); // 'send' or 'verify'
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const isEmail = type === 'email';

  const handleSendOTP = async () => {
    setSending(true);
    try {
      const endpoint = isEmail ? '/auth/send-email-otp' : '/auth/send-phone-otp';
      await api.post(endpoint);
      toast.success(`Verification code sent to your ${type}`);
      setStep('verify');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to send code'));
    } finally {
      setSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const endpoint = isEmail ? '/auth/verify-email-otp' : '/auth/verify-phone-otp';
      await api.post(endpoint, { code: otp });
      toast.success(`${isEmail ? 'Email' : 'Phone'} verified successfully!`);
      onSuccess();
      onClose();
      setStep('send');
      setOtp('');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Invalid or expired code'));
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    setStep('send');
    setOtp('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEmail ? <Mail className="w-5 h-5 text-blue-600" /> : <MessageSquare className="w-5 h-5 text-green-600" />}
            Verify {isEmail ? 'Email' : 'Phone'}
          </DialogTitle>
          <DialogDescription>
            {step === 'send' 
              ? `We'll send a verification code to your ${type}` 
              : `Enter the 6-digit code sent to your ${type}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {step === 'send' ? (
            <>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Click the button below to receive a verification code via {isEmail ? 'email' : 'SMS'}.
                  The code will be valid for 10 minutes.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendOTP}
                  disabled={sending}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <>Send Code</>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="text-center text-2xl tracking-widest"
                  autoFocus
                />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 Check your {isEmail ? 'email inbox' : 'phone messages'} for the verification code.
                  Code expires in 10 minutes.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('send')}
                  className="flex-1"
                >
                  Resend Code
                </Button>
                <Button
                  onClick={handleVerifyOTP}
                  disabled={verifying || otp.length !== 6}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                >
                  {verifying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                  ) : (
                    <>Verify</>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificationModal;
